import { memo, useCallback, useState } from "react";
import { Check } from "lucide-react";
import { COLOR_META, CYCLE_ORDER } from "@/lib/colors";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Cellule couleur : clic (ou Entrée/Espace) ouvre un menu déroulant visuel des 6 niveaux.
// Sélection directe du niveau souhaité (plus de cyclage). Sauvegarde automatique.
// Clavier : Entrée/Espace ouvre le menu ; ↑↓ dans le menu ; Entrée valide ; Échap ferme.
// Flèches (menu fermé) = navigation entre cellules.
export const ColorCell = memo(function ColorCell({
  passportId, criterion, value, studentName, rowIndex, colIndex,
  onSelect, onNavigate, registerCell, size = "md",
}) {
  const [open, setOpen] = useState(false);
  const meta = COLOR_META[value] || COLOR_META.white;
  const lg = size === "lg";

  const setRef = useCallback(
    (el) => registerCell(rowIndex, colIndex, el),
    [registerCell, rowIndex, colIndex]
  );

  // Menu fermé : les flèches déplacent le focus entre les cellules (on empêche Radix d'ouvrir).
  const handleKeyDown = useCallback((e) => {
    if (open) return; // menu ouvert : Radix gère ↑↓ / Entrée / Échap
    const map = { ArrowRight: "right", ArrowLeft: "left", ArrowUp: "up", ArrowDown: "down" };
    if (map[e.key]) {
      e.preventDefault();
      onNavigate(map[e.key], rowIndex, colIndex);
    }
    // Entrée / Espace : laisser Radix ouvrir le menu (comportement par défaut du trigger).
  }, [open, onNavigate, rowIndex, colIndex]);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        ref={setRef}
        onKeyDown={handleKeyDown}
        title={meta.meaning}
        aria-label={`${studentName}, ${criterion.replace(/_/g, " ")} : ${meta.meaning}. Entrée pour choisir le niveau.`}
        className={`${lg ? "h-12" : "h-9"} w-full rounded-md ring-offset-2 ring-offset-white transition-[background-color,transform] duration-100 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 data-[state=open]:ring-2 data-[state=open]:ring-slate-900 dark:ring-offset-[#0f0f0f] ${meta.cell}`}
        data-testid={`color-cell-${passportId}-${criterion}`}
        data-color={value}
      />
      <DropdownMenuContent align="start" className={lg ? "w-[calc(100vw-3rem)] max-w-sm" : "w-64"} data-testid={`color-menu-${passportId}-${criterion}`}>
        {CYCLE_ORDER.map((color) => {
          const m = COLOR_META[color];
          const active = color === value;
          return (
            <DropdownMenuItem
              key={color}
              onSelect={() => onSelect(passportId, criterion, color)}
              className={`flex items-center gap-2.5 ${lg ? "min-h-[48px] py-2.5" : ""} ${active ? "bg-slate-100 dark:bg-white/10" : ""}`}
              data-testid={`color-option-${passportId}-${criterion}-${color}`}
              data-color={color}
            >
              <span className={`${lg ? "h-5 w-5" : "h-4 w-4"} shrink-0 rounded-full ${m.dot}`} aria-hidden="true" />
              <span className={`flex-1 ${lg ? "text-base" : "text-sm"} text-slate-700 dark:text-slate-200`}>{m.meaning}</span>
              {active && <Check size={15} className="shrink-0 text-slate-500" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
});
